import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';

declare global {
  interface Window {
    grecaptcha?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => number;
      reset: (id?: number) => void;
    };
    __qsRecaptchaReady?: () => void;
  }
}

export const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

let loader: Promise<void> | null = null;

const loadScript = () => {
  if (window.grecaptcha?.render) return Promise.resolve();
  if (loader) return loader;
  loader = new Promise<void>((resolve, reject) => {
    window.__qsRecaptchaReady = () => resolve();
    const s = document.createElement('script');
    s.src = 'https://www.google.com/recaptcha/api.js?onload=__qsRecaptchaReady&render=explicit';
    s.async = true;
    s.defer = true;
    s.onerror = () => { loader = null; reject(new Error('Unable to load reCAPTCHA')); };
    document.head.appendChild(s);
  });
  return loader;
};

export type RecaptchaHandle = { reset: () => void };

type Props = { onChange: (token: string) => void };

/** Google reCAPTCHA v2 checkbox. Shows a notice instead when the site key is missing or the script cannot load. */
const Recaptcha = forwardRef<RecaptchaHandle, Props>(({ onChange }, ref) => {
  const holder = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<number | null>(null);
  const [failed, setFailed] = useState(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useImperativeHandle(ref, () => ({
    reset: () => {
      onChangeRef.current('');
      if (widgetId.current !== null) window.grecaptcha?.reset(widgetId.current);
    },
  }));

  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) return;
    let cancelled = false;
    loadScript()
      .then(() => {
        if (cancelled || !holder.current || widgetId.current !== null || !window.grecaptcha) return;
        widgetId.current = window.grecaptcha.render(holder.current, {
          sitekey: RECAPTCHA_SITE_KEY,
          callback: (token: string) => onChangeRef.current(token),
          'expired-callback': () => onChangeRef.current(''),
          'error-callback': () => onChangeRef.current(''),
        });
      })
      .catch(() => setFailed(true));
    return () => { cancelled = true; };
  }, []);

  if (!RECAPTCHA_SITE_KEY) {
    return (
      <Typography variant="caption" color="warning.main">
        reCAPTCHA is not configured: REACT_APP_RECAPTCHA_SITE_KEY is missing. Add it to website/.env and restart the website.
      </Typography>
    );
  }
  return (
    <>
      <Box ref={holder} sx={{ minHeight: 78 }} />
      {failed && (
        <Typography variant="caption" color="error">
          Could not load reCAPTCHA. Check your internet connection or ad blocker and reload the page.
        </Typography>
      )}
    </>
  );
});

export default Recaptcha;
